import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CloseIcon from '@material-ui/icons/Close';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import ImageIcon from '@material-ui/icons/Image';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HomeIcon from '@material-ui/icons/Home';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import styles from './DropboxFileBrowser.module.css';

const getFileIcon = (fileName, isFolder) => {
  if (isFolder) {
    return <FolderIcon />;
  }

  const extension = fileName.split('.').pop().toLowerCase();

  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
    return <VideoLibraryIcon />;
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return <ImageIcon />;
  }
  return <InsertDriveFileIcon />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
};

export const DropboxFileBrowser = ({ accessToken, onClose, onFilesSelected }) => {
  const [currentPath, setCurrentPath] = useState('');
  const [pathHistory, setPathHistory] = useState(['']);
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFolder = async (path) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/list_folder',
        {
          path: path || '',
          recursive: false,
          include_deleted: false,
          include_mounted_folders: true,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      // Filter to show only folders, videos, and photos
      const allItems = response.data.entries || [];
      const filteredItems = allItems.filter((item) => {
        // Always show folders
        if (item['.tag'] === 'folder') {
          return true;
        }

        // For files, check if they're videos or photos
        if (item['.tag'] === 'file') {
          const fileName = item.name.toLowerCase();
          const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'];
          const photoExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif', 'tiff', 'tif'];

          const extension = fileName.split('.').pop();
          return videoExtensions.includes(extension) || photoExtensions.includes(extension);
        }

        return false;
      });

      setItems(filteredItems);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading Dropbox folder:', err);
      setError('Failed to load folder contents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      loadFolder(currentPath);
    }
    // eslint-disable-next-line
  }, [currentPath, accessToken]);

  const handleFolderClick = (folderPath) => {
    setPathHistory([...pathHistory, folderPath]);
    setCurrentPath(folderPath);
  };

  const handleBackClick = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const previousPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      setCurrentPath(previousPath);
    }
  };

  const handleHomeClick = () => {
    setPathHistory(['']);
    setCurrentPath('');
  };

  const handleFileToggle = (file) => {
    const fileId = file.id;
    const isSelected = selectedFiles.some((f) => f.id === fileId);

    if (isSelected) {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, {
        id: fileId,
        name: file.name,
        size: file.size,
        path: file.path_display,
      }]);
    }
  };

  const handleImport = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  };

  const isFileSelected = (fileId) => selectedFiles.some((f) => f.id === fileId);

  const getCurrentFolderName = () => {
    if (!currentPath || currentPath === '') {
      return 'Dropbox';
    }
    return currentPath.split('/').pop();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Browse Dropbox</h2>
            <div className={styles.breadcrumb}>
              <button
                className={styles.breadcrumbButton}
                onClick={handleHomeClick}
                disabled={currentPath === ''}
              >
                <HomeIcon style={{ fontSize: 16 }} />
              </button>
              {currentPath && (
                <>
                  <span className={styles.breadcrumbSeparator}>/</span>
                  <span className={styles.breadcrumbCurrent}>{getCurrentFolderName()}</span>
                </>
              )}
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Navigation */}
        {pathHistory.length > 1 && (
          <div className={styles.navigation}>
            <button className={styles.backButton} onClick={handleBackClick}>
              <ArrowBackIcon style={{ marginRight: 8 }} />
              Back
            </button>
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button
                className={styles.retryButton}
                onClick={() => loadFolder(currentPath)}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <div className={styles.emptyState}>
              <FolderIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>This folder is empty</p>
            </div>
          )}

          {!isLoading && !error && items.length > 0 && (
            <div className={styles.itemList}>
              {items.map((item) => {
                const isFolder = item['.tag'] === 'folder';
                const isFile = item['.tag'] === 'file';
                const isSelected = isFileSelected(item.id);

                return (
                  <div
                    key={item.id}
                    className={`${styles.item} ${isFolder ? styles.itemFolder : ''} ${isSelected ? styles.itemSelected : ''}`}
                    onClick={() => {
                      if (isFolder) {
                        handleFolderClick(item.path_display);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isFolder) {
                        handleFolderClick(item.path_display);
                      }
                    }}
                  >
                    <div className={styles.itemIcon}>
                      {getFileIcon(item.name, isFolder)}
                    </div>

                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.name}</div>
                      {isFile && item.size && (
                        <div className={styles.itemSize}>{formatFileSize(item.size)}</div>
                      )}
                    </div>

                    {isFile && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileToggle(item);
                        }}
                      >
                        {isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedFiles.length > 0 && (
              <span className={styles.selectedCount}>
                {selectedFiles.length}
                {' '}
                file
                {selectedFiles.length !== 1 ? 's' : ''}
                {' '}
                selected
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles.importButton}
              onClick={handleImport}
              disabled={selectedFiles.length === 0}
            >
              Import
              {' '}
              {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
